import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useStartMeeting, useUser } from '../../hooks';
import { isHostedByTeamHub } from '../../services/hostAuthService';
import { focusMeetingWindow } from '../../services/meetingWindow/meetingWindowManager';
import { usePerformanceTracking } from '../../services/performanceMonitor';
import { AuthErrorBanner } from '../AuthErrorBanner';
import { AuthenticatedHomePage } from '../AuthenticatedHomePage';
import { BusinessUseCasesSection } from '../BusinessUseCasesSection';
//import { DownloadAppsSection } from '../DownloadAppsSection';
import { Footer } from '../Footer';
import { Header } from '../Header';
import { JoinMeetingModal } from '../JoinMeetingModal';
import { LazyPersonalMeetingsModal } from '../LazyComponents';
import MeetingInvitationCard from '../MeetingInvitationCardModal/MeetingInvitationCardModal';
import { ScheduleMeetingModal } from '../ScheduleMeetingModal';
import { SettingsModal } from '../SettingsModal';
import useScheduleMeetingInvite from './hooks/useScheduleMeetingInvite';

const HomePage: React.FC = () => {
  type SettingsTab = 'devices' | 'profile' | 'calendar' | 'meetings' | 'terms';
  const { trackInteraction } = usePerformanceTracking('HomePage');

  const { userInfo, isAuthenticated, login: logIn, logout: logOut, error: authError } = useUser();

  const [errorDismissed, setErrorDismissed] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<SettingsTab | undefined>(undefined);
  const [isPersonalMeetingsOpen, setIsPersonalMeetingsOpen] = useState(false);
  const [isScheduleMeetingOpen, setIsScheduleMeetingOpen] = useState(false);
  const [isJoinMeetingOpen, setIsJoinMeetingOpen] = useState(false);

  const { startMeeting: handleStartNewMeeting, isCreating: isNewMeetingPending } = useStartMeeting({
    trackInteraction,
  });

  const {
    meetingCard,
    dialInData,
    showInvite,
    isOtherCalendarInvite,
    handleSuccess: handleScheduleMeetingSuccess,
    closeInvite,
  } = useScheduleMeetingInvite();

  useEffect(() => {
    if (authError) {
      setErrorDismissed(false);
    }
  }, [authError]);

  useEffect(() => {
    const handleOpenSettingsModal = (event: Event) => {
      const customEvent = event as CustomEvent<{ tab?: SettingsTab }>;
      setSettingsInitialTab(customEvent.detail?.tab);
      setIsSettingsOpen(true);
    };

    window.addEventListener('open-settings-modal', handleOpenSettingsModal);
    return () => {
      window.removeEventListener('open-settings-modal', handleOpenSettingsModal);
    };
  }, []);

  const handleJoinMeeting = useCallback(() => {
    const endTracking = trackInteraction('join_meeting');
    // SM-1165: in hosted (MF) mode, an already-open meeting popup takes
    // priority over the Join dialog — focus it instead of prompting for
    // another meeting id.
    if (isHostedByTeamHub() && focusMeetingWindow()) {
      endTracking();
      return;
    }
    setIsJoinMeetingOpen(true);
    endTracking();
  }, [trackInteraction]);

  const handleScheduleMeeting = useCallback(() => {
    const endTracking = trackInteraction('schedule_meeting');
    setIsScheduleMeetingOpen(true);
    endTracking();
  }, [trackInteraction]);

  const handleOpenSettings = useCallback(() => {
    setSettingsInitialTab(undefined);
    setIsSettingsOpen(true);
  }, []);

  const handleOpenPersonalMeetings = useCallback(() => {
    setIsPersonalMeetingsOpen(true);
  }, []);

  return (
    <div id="LandingPage">
      {authError && !errorDismissed && (
        <AuthErrorBanner
          error={authError || 'An authentication error occurred.'}
          type="warning"
          onDismiss={() => setErrorDismissed(true)}
        />
      )}

      {showInvite && meetingCard && (
        <MeetingInvitationCard
          meetingCard={meetingCard}
          onClose={closeInvite}
          phoneNumber={dialInData?.phoneNumber}
          pin={dialInData?.pin}
          hasLicenseError={dialInData?.hasLicenseError}
          isOtherCalendar={isOtherCalendarInvite}
        />
      )}

      <div className={`home ${authError && !errorDismissed ? 'withBanner' : ''}`}>
        {/*
          The global NG Meet header is suppressed in TeamHub-hosted (MF) mode
          because TeamHub already provides its own chrome (logo, user menu,
          logout, settings). Hidden on every NG Meet route (this is the only
          place the header is rendered for the authenticated app). SM-1273:
          Personal Meetings sits here next to Settings in standalone; hosted
          mode surfaces it as a link above the action tiles instead.
        */}
        {!isHostedByTeamHub() && (
          <Header
            onLogout={logOut}
            onLogin={() => logIn()}
            onOpenSettings={handleOpenSettings}
            onOpenPersonalMeetings={handleOpenPersonalMeetings}
            userInfo={userInfo || undefined}
            isAuthenticated={isAuthenticated}
          />
        )}

        {isAuthenticated && userInfo ? (
          <AuthenticatedHomePage
            user={userInfo}
            onNewMeeting={handleStartNewMeeting}
            isNewMeetingPending={isNewMeetingPending}
            onJoinMeeting={handleJoinMeeting}
            onScheduleMeeting={handleScheduleMeeting}
            onPersonalMeetings={handleOpenPersonalMeetings}
            // SM-1165: Settings entry-point removed from MF (TeamHub-hosted)
            // mode — TeamHub's own chrome already provides settings access.
          />
        ) : (
          <>
            {/*<DownloadAppsSection />*/}
            <BusinessUseCasesSection />
            <Footer />
          </>
        )}
      </div>

      {isSettingsOpen && (
        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => {
            setIsSettingsOpen(false);
            setSettingsInitialTab(undefined);
          }}
          initialTab={settingsInitialTab}
        />
      )}

      {isPersonalMeetingsOpen && (
        <LazyPersonalMeetingsModal
          isOpen={isPersonalMeetingsOpen}
          onClose={() => setIsPersonalMeetingsOpen(false)}
        />
      )}

      {isScheduleMeetingOpen && (
        <ScheduleMeetingModal
          isOpen={isScheduleMeetingOpen}
          onClose={() => setIsScheduleMeetingOpen(false)}
          onSuccess={handleScheduleMeetingSuccess}
        />
      )}

      <JoinMeetingModal isOpen={isJoinMeetingOpen} onClose={() => setIsJoinMeetingOpen(false)} />
    </div>
  );
};

export default HomePage;
